/// <reference types="react" />

import * as React from "react";
import * as ReactDOM from "react-dom";

import { ActionCreatorManager } from "DistributedTaskControls/Common/Actions/ActionCreatorManager";
import { BuildBranchFilterComponent } from "DistributedTaskControls/Components/BuildBranchFilterComponent";
import { ComboBoxInputComponent, ComboBoxType } from "DistributedTaskControls/SharedControls/InputControls/Components/ComboBoxInputComponent";
import { Component, IProps, IState } from "DistributedTaskControls/Common/Components/Base";
import { Component as InformationBar } from "DistributedTaskControls/Components/InformationBar";
import { LoadingComponent } from "DistributedTaskControls/Components/LoadingComponent";
import { MessageHandlerActionsCreator } from "DistributedTaskControls/Actions/MessageHandlerActionsCreator";
import { StoreManager } from "DistributedTaskControls/Common/Stores/StoreManager";

import { BuildVersionPickerActionsCreator } from "PipelineWorkflow/Scripts/SharedComponents/CreateRelease/BuildVersionPickerActionsCreator";
import { BuildVersionPickerStore, IBuildVersionPickerDialogState } from "PipelineWorkflow/Scripts/SharedComponents/CreateRelease/BuildVersionPickerStore";
import { ContributionIds } from "PipelineWorkflow/Scripts/Common/Constants";
import { CreateReleaseArtifactConstants } from "PipelineWorkflow/Scripts/SharedComponents/CreateRelease/Constants";
import { FeatureFlagUtils } from "PipelineWorkflow/Scripts/Shared/Utils/FeatureFlagUtils";
import { MessageBarKeys } from "PipelineWorkflow/Scripts/SharedComponents/Dialogs/Constants";
import * as Resources from "PipelineWorkflow/Scripts/Resources/TFS.Resources.PipelineWorkflow";
import * as PipelineTypes from "PipelineWorkflow/Scripts/Common/Types";

import { DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { Dialog, DialogFooter, DialogType } from "OfficeFabric/Dialog";
import { MessageBarType } from "OfficeFabric/MessageBar";
import { Label } from "OfficeFabric/Label";
import { SpinnerSize } from "OfficeFabric/Spinner";
import { css } from "VSSUI/Util";

import { getErrorMessage } from "VSS/VSS";
import * as SDK_Shim from "VSS/SDK/Shim";
import * as Utils_String from "VSS/Utils/String";

import "VSS/LoaderPlugins/Css!RM:PipelineWorkflow/Scripts/SharedComponents/CreateRelease/BuildVersionPickerDialog";

export interface IBuildVersionPickerDialogProps extends IProps {
    artifact: PipelineTypes.PipelineArtifact;
    onCallbackVersionSelected: (version: PipelineTypes.IArtifactVersion) => void;
    onCloseDialog: () => void;
}

export class BuildVersionPickerDialog extends Component<IBuildVersionPickerDialogProps, IBuildVersionPickerDialogState> {
    constructor(props: IBuildVersionPickerDialogProps) {
        super(props);

        this._actionsCreator = ActionCreatorManager.GetActionCreator<BuildVersionPickerActionsCreator>(BuildVersionPickerActionsCreator);
        this._messageHandlerActionCreator = ActionCreatorManager.GetActionCreator<MessageHandlerActionsCreator>(MessageHandlerActionsCreator);
        this._versionPickerStore = StoreManager.GetStore<BuildVersionPickerStore>(BuildVersionPickerStore);
    }

    public componentWillMount() {
        this._messageHandlerActionCreator.dismissMessage(MessageBarKeys.BuildVersionPickerDialog);
        this._versionPickerStore.addChangedListener(this._onStateUpdate);
        this._actionsCreator.initializeData(
            this.props.artifact, 
            (error) => {
                this._messageHandlerActionCreator.addMessage(
                    MessageBarKeys.BuildVersionPickerDialog, 
                    getErrorMessage(error), 
                    MessageBarType.error
                );
            }
        );
    }

    public componentWillUnmount() {
        this._versionPickerStore.removeChangedListener(this._onStateUpdate);
    }

    public static showDialog(context: PipelineTypes.IVersionPickerMenuItemContext): void {
        const dialogContainer = document.createElement("div");

        ReactDOM.render(
            <BuildVersionPickerDialog
                artifact={context.artifact}
                onCallbackVersionSelected={context.onVersionSelected}
                onCloseDialog={() => { ReactDOM.unmountComponentAtNode(dialogContainer); }}
            >
                <InformationBar 
                    parentKey={ MessageBarKeys.BuildVersionPickerDialog } 
                />
            </BuildVersionPickerDialog>,
            dialogContainer
        );
    }

    public render(): JSX.Element {
        return (
            <Dialog
                hidden={false}
                dialogContentProps={{
                    type: DialogType.close,
                    title: Resources.BranchVersionPickerText,
                    titleId: "build-version-picker-dialog-title-id",
                }}
                modalProps={{
                    className: css("build-version-picker-dialog"),
                    containerClassName: css("build-version-picker-dialog-container"),
                    isBlocking: true
                }}
                onDismiss={this._onCloseDialog}
                closeButtonAriaLabel={Resources.CloseText} >
                <Label className="branch-input-label">
                    {Resources.BranchLabel}
                </Label>
                <BuildBranchFilterComponent
                    key={"build-branch-key"}
                    repositoryId={this.state.repositoryId}
                    branchFilter={this.state.selectedBranch}
                    onBranchFilterChange={(branch: string) => { this._onBranchSelectionChange(branch); }}
                    allowUnmatchedSelection={false}
                    supportVariables={false}
                    updateOnBlur={true} 
                />
                {this._getBuildVersionsSection()}
                <DialogFooter>
                    <PrimaryButton
                        onClick={this._onOkClick}
                        disabled={!this.state.isValid}
                        ariaLabel={Resources.AllCapsOkText}>
                        {Resources.AllCapsOkText}
                    </PrimaryButton>
                    <DefaultButton
                        onClick={this._onCloseDialog}
                        disabled={false}
                        ariaLabel={Resources.CancelText}>
                        {Resources.CancelText}
                    </DefaultButton>
                </DialogFooter>
            </Dialog>
        );
    }

    private _getBuildVersionsSection(): JSX.Element {
        if (this.state.isBuildVersionsDataLoaded) {
            return (              
                <div className="build-versions-section">
                    <Label className="build-version-input-label">
                        {Resources.BuildVersionLabel}
                    </Label>
                    <ComboBoxInputComponent
                        source={this._getBuildVersionsName()}
                        value={this.state.selectedVersion.name}
                        onValueChanged={(item) => this._onBuildVersionChange(item)}
                        comboBoxType={ComboBoxType.Editable}
                        required={true}
                        allowEdit={false} // if the buildId is known then that can be directly given as input in artifact selection
                        textfieldOnEmptySource={false}
                        errorMessage={Resources.SettingsRequired}
                    />
                </div>
            );
        } else {
            return (              
                <LoadingComponent className="build-versions-loading" size={SpinnerSize.large} />
            );
        }
    }

    private _getBuildVersionsName(): string[] {
        return this.state.buildVersions.map(v => v.name);
    }

    private _closeDialog(): void {
        if (this.props.onCloseDialog) {
            this.props.onCloseDialog();
        }
    }

    private _onBranchSelectionChange(branch: string): void {
        this._messageHandlerActionCreator.dismissMessage(MessageBarKeys.BuildVersionPickerDialog);
        this._actionsCreator.setSelectedBranch(branch);
        this._actionsCreator.getBuildVersions(
            this.props.artifact, 
            branch,
            (error) => {
                this._messageHandlerActionCreator.addMessage(
                    MessageBarKeys.BuildVersionPickerDialog, 
                    getErrorMessage(error), 
                    MessageBarType.error);
            }
        );
    }

    private _onBuildVersionChange(version: string): void {
        const selectedBuildVersion : PipelineTypes.IArtifactVersion = this.state.buildVersions.find(v => Utils_String.localeIgnoreCaseComparer(v.name, version) === 0);
        this._actionsCreator.setSelectedBuildVersion(selectedBuildVersion);
    }

    private _onCloseDialog = (): void => {
        this._closeDialog();
    }

    private _onOkClick = (): void => {
        this.props.onCallbackVersionSelected(this.state.selectedVersion);
        this._closeDialog();
    }

    private _onStateUpdate = (): void => {
        this.setState(this._versionPickerStore.getState());
    }

    private _actionsCreator: BuildVersionPickerActionsCreator;
    private _messageHandlerActionCreator: MessageHandlerActionsCreator;
    private _versionPickerStore: BuildVersionPickerStore;
}

SDK_Shim.registerContent("create-release-build-version-picker", (context) => {
    SDK_Shim.VSS.register(ContributionIds.CreateReleaseBuildVersionPickerMenuContributionId, { 
        execute: (menuItemContext: PipelineTypes.IVersionPickerMenuItemContext) => {
            if (FeatureFlagUtils.isArtifactVersionPickerEnabled()) {
                BuildVersionPickerDialog.showDialog(menuItemContext);
            }
        }
    });
});